"use client"

import { useMemo } from "react"
import { RiCalendarEventLine } from "@remixicon/react"
import { addDays, isToday } from "date-fns"

import { AgendaDaysToShow } from "./constants"
import { EventItem } from "./event-item"
import { getAgendaEventsForDay } from "./utils"
import { formatDateString, resolveTimeZone } from "./time-utils"

/**
 * @typedef {import("./types").CalendarEvent} CalendarEvent
 */

/**
 * @param {{
 *   currentDate: Date,
 *   events: CalendarEvent[],
 *   onEventSelect: (event: CalendarEvent) => void
 * }} props
 */
export function AgendaView({
  currentDate,
  events,
  onEventSelect,
  timezone,
  timeFormat = "24h",
}) {
  const resolvedTimeZone = resolveTimeZone(timezone)
  const days = useMemo(() => {
    return Array.from({ length: AgendaDaysToShow }, (_, i) =>
      addDays(new Date(currentDate), i)
    )
  }, [currentDate])

  const handleEventClick = (event, e) => {
    e.stopPropagation()
    onEventSelect(event)
  }

  const hasEvents = days.some(
    (day) => getAgendaEventsForDay(events, day).length > 0
  )

  return (
    <div className="border-border/70 border-t px-4">
      {!hasEvents ? (
        <div className="flex min-h-[70svh] flex-col items-center justify-center py-16 text-center">
          <RiCalendarEventLine
            size={32}
            className="text-muted-foreground/50 mb-2"
          />
          <h3 className="text-lg font-medium">No events found</h3>
          <p className="text-muted-foreground">
            There are no events scheduled for this time period.
          </p>
        </div>
      ) : (
        days.map((day) => {
          const dayEvents = getAgendaEventsForDay(events, day)

          if (dayEvents.length === 0) return null

          return (
            <div
              key={day.toString()}
              className="border-border/70 relative my-12 border-t"
            >
              <span
                className="bg-background absolute -top-3 left-0 flex h-6 items-center pe-4 text-[10px] uppercase data-today:font-medium sm:pe-4 sm:text-xs"
                data-today={isToday(day) || undefined}
              >
                {formatDateString(day, resolvedTimeZone, {
                  day: "numeric",
                  month: "short",
                  weekday: "long",
                })}
              </span>
              <div className="mt-6 space-y-2">
                {dayEvents.map((event) => (
                  <EventItem
                    key={event.id}
                    event={event}
                    view="agenda"
                    onClick={(e) => handleEventClick(event, e)}
                    timezone={resolvedTimeZone}
                    timeFormat={timeFormat}
                  />
                ))}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}
